import { map } from 'lodash-es'
import { useState } from 'react'

import Loader from '../../../common/components/Loader'
import { DownloadVouchers } from '../../../common/models/rewards'
import { IColumn, Table } from '../../../core/components/Table'
import { downloadVoucherBatch } from '../../../core/services/voucher'
import { MessageBox } from '../messages/MessageBox'
import { Pagination } from '../Pagination'
import { Checkbox } from '@/components/ui'

export interface VoucherDownloadsTableProps {
  isCheck?: string[]
  setIsCheck?: (check: string[]) => void
  vouchers: DownloadVouchers[]
  page: number
  setPage: (p: number) => void
  size: number
  setSize: (s: number) => void
  sortBy?: string
  setSortBy: (s: string) => void
  totalElements: number
  setTotalElemenets: (t: number) => void
  withPagination?: boolean
}

export const VoucherDownloadsTable = (
  props: VoucherDownloadsTableProps,
): JSX.Element => {
  const {
    vouchers,
    size,
    page,
    setPage,
    setSize,
    sortBy,
    setSortBy,
    totalElements,
    withPagination,
    setIsCheck,
    isCheck,
  } = props

  const [isCheckAll, setIsCheckAll] = useState(false)
  const [loading, setLoading] = useState<boolean>(false)

  const [isError, setIsError] = useState<boolean>(false)
  const [errorStatus, setErrorStatus] = useState<number | undefined>(undefined)
  const [showMessageBox, setShowMessageBox] = useState<boolean>(false)
  const [message, setMessage] = useState<string>('')

  const handleSelectAll = () => {
    setIsCheckAll(!isCheckAll)
    setIsCheck?.(vouchers.map((voucher) => voucher.id))
    if (isCheckAll) {
      setIsCheck?.([])
    }
  }
  const handleClick = (id: string) => {
    if (isCheck) {
      setIsCheck?.([...isCheck, id])
    } else {
      setIsCheck?.([])
    }
  }
  const handleSortBy = (sort: string) => {
    if (sort === sortBy) {
      setSortBy('updatedAt')
    } else {
      setSortBy(sort)
    }
  }

  const pageChange = async (newPage: number) => {
    try {
      setPage(newPage)
    } catch (e) {
      if (e instanceof Error) {
        setIsError(true)
        setShowMessageBox(true)
        setMessage(e.message)
        setErrorStatus(
          (e as { response?: { status?: number } })?.response?.status,
        )
      } else {
        console.error(e)
      }
    }
  }

  const getColor = (status: string) => {
    switch (status) {
      case 'PROCESSING':
        return '#CC9A06'
      case 'COMPLETED':
        return '#198754'
      case 'FAILED':
        return '#DC3545'
      default:
        return '#CC9A06'
    }
  }

  const downloadBatch = async (
    id: string,
    type: 'PNG' | 'PDF' | 'SVG' | 'CSV',
  ) => {
    setLoading(true)
    try {
      const response = await downloadVoucherBatch(id, type)
      const href = URL.createObjectURL(response.data)

      // create "a" HTML element with href to file & click
      const link = document.createElement('a')
      link.href = href
      link.setAttribute('download', type === 'CSV' ? `${id}.csv` : `${id}.zip`) //or any other extension
      document.body.appendChild(link)
      link.click()

      // clean up "a" element & remove ObjectURL
      document.body.removeChild(link)
      URL.revokeObjectURL(href)
    } catch (e) {
      if (e instanceof Error) {
        setIsError(true)
        setShowMessageBox(true)
        setMessage(e.message)
        setErrorStatus(
          (e as { response?: { status?: number } })?.response?.status,
        )
      } else {
        console.error(e)
      }
    } finally {
      setLoading(false)
    }
  }

  const columns: IColumn[] = [
    {
      title: (
        <Checkbox onCheckedChange={handleSelectAll} checked={isCheckAll} />
      ),
      accessor: '',
      render: (row) => (
        <Checkbox
          onCheckedChange={() => handleClick(row.id)}
          checked={isCheck?.includes(row.id)}
        />
      ),
      width: '5%',
    },
    {
      title: 'Voucher Name',
      accessor: 'voucherName',
      sortable: true,
      render: (row) => (
        <div className="flex flex-col">
          <span>{row.label}</span>
          <div className="flex gap-2">
            {(row.pdfEnabled || row.status === 'FAILED') && (
              <>
                <button
                  className="view p-0"
                  disabled={['FAILED', 'PROCESSING'].includes(row.status)}
                  onClick={() => downloadBatch(row.id, 'PDF')}
                >
                  PDF
                </button>
                {(row.pngEnabled ||
                  row.csvEnabled ||
                  row.svgEnabled ||
                  row.status === 'FAILED') && <span>|</span>}
              </>
            )}
            {(row.pngEnabled || row.status === 'FAILED') && (
              <>
                <button
                  className="view p-0"
                  disabled={['FAILED', 'PROCESSING'].includes(row.status)}
                  onClick={() => downloadBatch(row.id, 'PNG')}
                >
                  PNG
                </button>
                {(row.csvEnabled ||
                  row.svgEnabled ||
                  row.status === 'FAILED') && <span>|</span>}
              </>
            )}
            {(row.svgEnabled || row.status === 'FAILED') && (
              <>
                <button
                  disabled={['FAILED', 'PROCESSING'].includes(row.status)}
                  className="view p-0"
                  onClick={() => downloadBatch(row.id, 'SVG')}
                >
                  SVG
                </button>
                {(row.csvEnabled || row.status === 'FAILED') && <span>|</span>}
              </>
            )}
            {(row.csvEnabled || row.status === 'FAILED') && (
              <button
                // size="sm"
                // variant="link"
                disabled={['FAILED', 'PROCESSING'].includes(row.status)}
                className="view p-0"
                onClick={() => downloadBatch(row.id, 'CSV')}
              >
                CSV
              </button>
            )}
          </div>
        </div>
      ),
      width: '30%',
    },
    {
      title: 'Tickets',
      accessor: 'tickets',
      render: (row) => (
        <div className="flex flex-col">
          <div className="flex flex-col">
            {map(row.tickets, (ticket, index) => {
              return (
                <div
                  key={`${ticket.category.name}-${ticket.ticket.name}-${index}`}
                  className="flex gap-2"
                >
                  <Checkbox checked disabled />
                  <span>{`${ticket.category.name}, ${ticket.ticket.name}`}</span>
                </div>
              )
            })}
          </div>
        </div>
      ),
      width: '30%',
    },
    {
      title: 'Created by',
      accessor: 'createdBy',
      sortable: true,
    },
    {
      title: 'Status',
      accessor: 'status',
      sortable: true,
      render: (row) => (
        <span style={{ color: getColor(row.status) }}>{row.status}</span>
      ),
    },
  ]

  return (
    <>
      {loading && <Loader />}
      <Table
        className="text-nowrap"
        data={vouchers}
        handleSortBy={handleSortBy}
        columns={columns}
        isCheck={isCheck}
        columnWidth="20%"
      />
      {withPagination && (
        <Pagination
          size={size}
          totalElements={totalElements}
          setSize={setSize}
          page={page + 1}
          changePage={pageChange}
        />
      )}
      <MessageBox
        message={message}
        setShowMessageBox={setShowMessageBox}
        showMessageBox={showMessageBox}
        isError={isError}
        errorStatus={errorStatus}
      />
    </>
  )
}
